import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiClient } from "../../utils/apiClient";
import { PublicArtworkEmbed } from "../../types/artworksData";
import { getProxiedImageUrl } from "../../utils/imageProxy";
import { ModelViewerEmbed } from "../../components/3d/model.viewer.embed";
import styles from "./artwork.embed.module.css";
import { trackArtworkView } from "../../utils/telemetry";

const BASE_API = process.env.REACT_APP_BASE_API || "";
const apiClient = ApiClient(BASE_API);
const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".gif", ".avif"];

const isImageType = (artwork?: PublicArtworkEmbed | null) => artwork?.type === "image";
const isVideoType = (artwork?: PublicArtworkEmbed | null) => artwork?.type === "video";
const isSoundType = (artwork?: PublicArtworkEmbed | null) => artwork?.type === "sound";
const isModelType = (artwork?: PublicArtworkEmbed | null) => artwork?.type === "3dmodel";

const isImageUrl = (value?: string | null): boolean => {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    const pathname = (parsed.pathname || "").toLowerCase();
    return IMAGE_EXTENSIONS.some((ext) => pathname.endsWith(ext));
  } catch (_error) {
    return false;
  }
};

const toImageSrc = (url?: string | null): string => {
  if (!url) return "";
  return getProxiedImageUrl(url, BASE_API);
};

export const ArtworkEmbedPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [artwork, setArtwork] = useState<PublicArtworkEmbed | null>(null);
  const [error, setError] = useState<string>("");
  const [viewerFailed, setViewerFailed] = useState<boolean>(false);

  useEffect(() => {
    const loadArtwork = async () => {
      if (!id) {
        setError("Artwork not found");
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await apiClient.getArtworkPublicById(id);
      if (!response || response.status === false || !response.data) {
        setError("Artwork not found");
        setArtwork(null);
        setLoading(false);
        return;
      }

      setArtwork(response.data);
      setError("");
      setLoading(false);
    };

    loadArtwork();
  }, [id]);

  useEffect(() => {
    setViewerFailed(false);
  }, [artwork?.id, artwork?.media?.modelUrl]);

  useEffect(() => {
    if (!artwork?.id) {
      return;
    }

    trackArtworkView(String(artwork.id), window.location.pathname + window.location.search);
  }, [artwork?.id]);

  const imagePreviewUrl = useMemo(() => {
    if (!artwork) return "";

    if (isModelType(artwork)) {
      if (artwork.media.thumbnailUrl) return toImageSrc(artwork.media.thumbnailUrl);
      if (isImageUrl(artwork.media.dataUrl)) return toImageSrc(artwork.media.dataUrl);
      return "";
    }

    if (isImageType(artwork)) {
      return toImageSrc(artwork.media.dataUrl || artwork.media.thumbnailUrl);
    }

    return toImageSrc(artwork.media.thumbnailUrl);
  }, [artwork]);

  const directModelUrl = artwork?.media?.modelUrl || (isModelType(artwork) ? artwork?.media?.dataUrl || "" : "");
  const audioBackgroundUrl = isSoundType(artwork) ? imagePreviewUrl : "";
  const canUseModelViewer = Boolean(
    isModelType(artwork) &&
    !viewerFailed &&
    artwork?.media?.directModelEligible &&
    directModelUrl
  );

  return (
    <div className={styles.root}>
      <div
        className={styles.media}
        data-has-audio-background={Boolean(audioBackgroundUrl)}
        style={audioBackgroundUrl ? { backgroundImage: `linear-gradient(180deg, rgba(8, 14, 26, 0.32) 0%, rgba(8, 14, 26, 0.84) 100%), url(${audioBackgroundUrl})` } : undefined}
      >
        <div className={styles.mediaFrame}>
          {loading ? (
            <div className={styles.placeholder}>Loading artwork...</div>
          ) : error ? (
            <div className={styles.placeholder}>{error}</div>
          ) : canUseModelViewer ? (
            <ModelViewerEmbed
              modelUrl={directModelUrl}
              className={styles.viewer}
              onError={() => setViewerFailed(true)}
            />
          ) : isImageType(artwork) && imagePreviewUrl ? (
            <img src={imagePreviewUrl} alt={artwork?.title || "Artwork"} />
          ) : isVideoType(artwork) && artwork?.media?.dataUrl ? (
            <video src={artwork.media.dataUrl} controls preload="metadata" />
          ) : isSoundType(artwork) && artwork?.media?.dataUrl ? (
            <div className={styles.audioFrame}>
              <audio src={artwork.media.dataUrl} controls />
            </div>
          ) : imagePreviewUrl ? (
            <img src={imagePreviewUrl} alt={artwork?.title || "Artwork"} />
          ) : isModelType(artwork) && directModelUrl ? (
            <div className={styles.placeholder}>
              <a
                href={directModelUrl}
                target="_blank"
                rel="noreferrer"
                className={styles.link}
              >
                Open 3D model
              </a>
            </div>
          ) : (
            <div className={styles.placeholder}>Preview unavailable</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtworkEmbedPage;
